<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <h6>{{ isEdit ? "編輯文章" : "新增文章" }}</h6>
          </div>
          <div class="card-body">
            <form @submit.prevent="handleSubmit">
              <div class="row">
                <div class="col-md-8">
                  <div class="form-group">
                    <label for="title" class="form-control-label">標題 *</label>
                    <input
                      class="form-control"
                      type="text"
                      id="title"
                      v-model="form.title"
                      required
                    />
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="status" class="form-control-label"
                      >狀態 *</label
                    >
                    <select
                      class="form-control"
                      id="status"
                      v-model="form.status"
                      required
                    >
                      <option value="draft">草稿</option>
                      <option value="published">已發布</option>
                      <option value="scheduled">排程</option>
                      <option value="archived">封存</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-12">
                  <div class="form-group">
                    <label for="summary" class="form-control-label">摘要</label>
                    <textarea
                      class="form-control"
                      id="summary"
                      rows="2"
                      v-model="form.summary"
                    ></textarea>
                  </div>
                </div>
                <div class="col-md-12">
                  <div class="form-group">
                    <div
                      class="d-flex justify-content-between align-items-center mb-2"
                    >
                      <label for="content" class="form-control-label mb-0"
                        >內容 *</label
                      >
                      <div class="btn-group btn-group-sm">
                        <button
                          type="button"
                          class="btn"
                          :class="
                            showGuide ? 'btn-primary' : 'btn-outline-primary'
                          "
                          @click="showGuide = !showGuide"
                        >
                          Markdown 指引
                        </button>
                        <button
                          type="button"
                          class="btn"
                          :class="
                            showPreview ? 'btn-primary' : 'btn-outline-primary'
                          "
                          @click="showPreview = !showPreview"
                        >
                          {{ showPreview ? "隱藏預覽" : "顯示預覽" }}
                        </button>
                      </div>
                    </div>

                    <!-- Markdown Guide -->
                    <div
                      v-if="showGuide"
                      class="alert alert-light border mb-3 p-3"
                    >
                      <div
                        class="d-flex justify-content-between align-items-center mb-2"
                      >
                        <h6 class="mb-0">Markdown 快速指引</h6>
                        <button
                          type="button"
                          class="btn-close"
                          aria-label="Close"
                          @click="showGuide = false"
                        ></button>
                      </div>
                      <div class="row text-sm">
                        <div class="col-md-6">
                          <ul class="mb-0 ps-3">
                            <li>
                              <strong># 標題 1</strong> ,
                              <strong>## 標題 2</strong>
                            </li>
                            <li><strong>**粗體**</strong> , <em>*斜體*</em></li>
                            <li><strong>- 列表項目</strong></li>
                            <li><strong>1. 有序列表</strong></li>
                          </ul>
                        </div>
                        <div class="col-md-6">
                          <ul class="mb-0 ps-3">
                            <li><strong>[連結文字](網址)</strong></li>
                            <li><strong>![圖片說明](圖片網址)</strong></li>
                            <li><strong>![尺寸](url =100x100)</strong></li>
                            <li><strong>> 引用區塊</strong></li>
                            <li><strong>```程式碼區塊```</strong></li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div class="row">
                      <!-- Editor Pane -->
                      <div :class="showPreview ? 'col-md-6' : 'col-md-12'">
                        <textarea
                          class="form-control"
                          rows="20"
                          v-model="form.content"
                          placeholder="請輸入 Markdown 格式內容..."
                          style="
                            font-family: monospace;
                            font-size: 14px;
                            min-height: 500px;
                          "
                        ></textarea>
                      </div>

                      <!-- Preview Pane -->
                      <div v-show="showPreview" class="col-md-6">
                        <div
                          class="border p-4 rounded bg-white h-100"
                          style="
                            min-height: 500px;
                            max-height: 800px;
                            overflow-y: auto;
                          "
                        >
                          <div
                            v-if="!form.content"
                            class="text-muted text-center py-5"
                          >
                            預覽區域
                          </div>
                          <div
                            v-else
                            v-html="renderMarkdown(form.content)"
                            class="markdown-body"
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Removed duplicate col-md-12 closing div from original messy structure if any, ensuring clean nesting -->

                <div class="col-md-6">
                  <div class="form-group">
                    <label for="category_id" class="form-control-label"
                      >分類</label
                    >
                    <select
                      class="form-control"
                      id="category_id"
                      v-model="form.category_id"
                    >
                      <option value="">無分類</option>
                      <option
                        v-for="cat in categories"
                        :key="cat.id"
                        :value="cat.id"
                      >
                        {{ cat.name }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="featured_image" class="form-control-label"
                      >特色圖片</label
                    >
                    <input
                      class="form-control"
                      type="file"
                      id="featured_image"
                      @change="handleFileUpload"
                      accept="image/*,.heic,.heif"
                    />
                    <div v-if="previewUrl" class="mt-2">
                      <img
                        :src="previewUrl"
                        class="img-thumbnail"
                        style="max-height: 150px"
                      />
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label class="form-control-label">精選文章</label>
                    <div class="form-check form-switch">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        id="is_featured"
                        v-model="form.is_featured"
                      />
                      <label class="form-check-label" for="is_featured"
                        >設為精選</label
                      >
                    </div>
                  </div>
                </div>
              </div>
              <div class="d-flex justify-content-end mt-4">
                <button
                  type="button"
                  class="btn btn-light me-2"
                  @click="$router.push('/blog')"
                >
                  取消
                </button>
                <button
                  type="submit"
                  class="btn btn-primary"
                  :disabled="loading"
                >
                  {{ loading ? "儲存中..." : "儲存" }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from "@/utils/request";
import { marked } from "marked";
import Swal from "sweetalert2";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

export default {
  name: "BlogForm",
  components: {},
  setup() {
    const route = useRoute();
    const router = useRouter();

    const loading = ref(false);
    const categories = ref([]);
    const form = ref({
      title: "",
      summary: "",
      content: "",
      category_id: "",
      status: "draft",
      is_featured: false,
      featured_image: null,
    });

    const showPreview = ref(true);
    const showGuide = ref(false);
    const previewUrl = ref(null);

    const isEdit = computed(() => route.params.id !== undefined);

    const renderer = new marked.Renderer();
    const originalImageRenderer = renderer.image;
    renderer.image = (href, title, text) => {
      if (title) {
        const sizeMatch = title.match(/^=(\d+|)(x(\d+|))?$/);
        if (sizeMatch) {
          let width = sizeMatch[1];
          let height = sizeMatch[3];
          let style = "";
          if (width) style += `width: ${width}px; `;
          if (height) style += `height: ${height}px; `;

          return `<img src="${href}" alt="${text}" style="${style}" class="img-fluid">`;
        }
      }
      return originalImageRenderer.call(renderer, href, title, text);
    };

    const renderMarkdown = (text) => {
      return marked(text || "", { renderer });
    };

    const fetchCategories = async () => {
      try {
        const response = await request.get("/api/admin/blog/categories");
        categories.value = response.data.data || response.data;
      } catch (error) {
        console.error("Failed to fetch categories", error);
      }
    };

    const fetchPost = async (id) => {
      try {
        const response = await request.get(`/api/admin/blog/posts/${id}`);
        const data = response.data.data;
        form.value = {
          title: data.title,
          summary: data.summary,
          content: data.content,
          category_id: data.category_id || "",
          status: data.status,
          is_featured: !!data.is_featured,
        };
        if (data.featured_image) {
          form.value.featured_image = data.featured_image;
          previewUrl.value = data.featured_image;
        }
      } catch (error) {
        console.error("Failed to fetch post", error);
        Swal.fire("錯誤", "載入失敗", "error");
      }
    };

    const handleFileUpload = async (event) => {
      const file = event.target.files[0];
      if (file) {
        try {
          const formData = new FormData();
          formData.append("file", file);
          const response = await request.post("/api/admin/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });

          if (response.data.success) {
            form.value.featured_image = response.data.url;
            previewUrl.value = response.data.url;
          } else {
            Swal.fire("錯誤", "上傳失敗", "error");
          }
        } catch (error) {
          console.error("Upload failed", error);
          Swal.fire("錯誤", "上傳失敗", "error");
        }
      }
    };

    const handleSubmit = async () => {
      loading.value = true;
      try {
        const data = {
          title: form.value.title,
          content: form.value.content,
          summary: form.value.summary,
          category_id: form.value.category_id,
          status: form.value.status,
          is_featured: form.value.is_featured,
          featured_image: form.value.featured_image,
        };

        if (isEdit.value) {
          await request.put(`/api/admin/blog/posts/${route.params.id}`, data);
        } else {
          await request.post("/api/admin/blog/posts", data);
        }

        router.push("/blog");
      } catch (error) {
        console.error("Failed to save post", error);
        Swal.fire("錯誤", error.response?.data?.message || "儲存失敗", "error");
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      fetchCategories();
      if (isEdit.value) {
        fetchPost(route.params.id);
      }
    });

    return {
      loading,
      categories,
      form,
      showPreview,
      showGuide,
      previewUrl,
      isEdit,
      renderMarkdown,
      handleFileUpload,
      handleSubmit,
    };
  },
};
</script>
